/**
 * Not Found Page - UI Component
 *
 * 404 page for routes that don't match.
 *
 * @layer pages
 * @slice error
 * @segment ui
 * @added DOC-0099 (2025-12-26)
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Heading, Stack, Text } from '@shared/ui';

interface NotFoundPageProps {
  /** Current locale for navigation */
  locale: string;
}

export function NotFoundPage({ locale }: NotFoundPageProps) {
  const { t } = useTranslation();
  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg">
        <CardContent className="p-8 text-center">
          <Stack gap="lg" className="items-center">
            <Text as="div" className="text-7xl font-bold text-muted-foreground/30">
              404
            </Text>
            <div className="space-y-2">
              <Heading as="h1" size="2xl">
                {t('notFound.title')}
              </Heading>
              <Text color="muted">{t('notFound.description')}</Text>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <Button asChild>
                <Link to="/$locale" params={{ locale }}>
                  {t('notFound.backHome')}
                </Link>
              </Button>
              <Button type="button" variant="outline" onClick={handleGoBack}>
                {t('notFound.back')}
              </Button>
            </div>
          </Stack>
        </CardContent>
      </Card>
    </div>
  );
}
